/**
 * Source Ranking + Budget Guidance
 *
 * DB-agnostic scoring helpers used by Growth Channels.
 * Contains no seed/demo/static dataset dependencies.
 */

export type SourceRank = "top_performer" | "average" | "underperforming";

export interface SourceRankResult {
  rank: SourceRank;
  label: string;
  badgeClass: string;
  textClass: string;
}

export function rankSource(
  netConversionPct: number,
  cpa: number,
  leadQualityPct: number,
): SourceRankResult {
  let score = 0;
  if (netConversionPct >= 40) score += 3;
  else if (netConversionPct >= 20) score += 2;
  else if (netConversionPct >= 10) score += 1;

  if (cpa > 0 && cpa <= 200) score += 3;
  else if (cpa > 0 && cpa <= 350) score += 2;
  else if (cpa > 0 && cpa <= 500) score += 1;

  if (leadQualityPct >= 75) score += 3;
  else if (leadQualityPct >= 60) score += 2;
  else if (leadQualityPct >= 40) score += 1;

  if (score >= 7) {
    return {
      rank: "top_performer",
      label: "Top Performer",
      badgeClass: "bg-green-500/10 text-green-700 border-green-500/20",
      textClass: "text-green-600",
    };
  }
  if (score >= 4) {
    return {
      rank: "average",
      label: "Average",
      badgeClass: "bg-amber-500/10 text-amber-700 border-amber-500/20",
      textClass: "text-amber-600",
    };
  }
  return {
    rank: "underperforming",
    label: "Underperforming",
    badgeClass: "bg-destructive/10 text-destructive border-destructive/20",
    textClass: "text-destructive",
  };
}

export interface BudgetRecommendation {
  message: string;
  severity: "positive" | "neutral" | "warning";
  icon: "increase" | "monitor" | "reduce";
}

export function getBudgetRecommendation(
  rank: SourceRank,
  cpa: number,
  leadQualityPct: number,
  roi: number,
): BudgetRecommendation {
  if (rank === "top_performer" && roi > 200) {
    return { message: "Increase budget — strong performance and high ROI", severity: "positive", icon: "increase" };
  }
  if (rank === "top_performer") {
    return { message: "Increase budget — consistent conversion and quality", severity: "positive", icon: "increase" };
  }
  if (rank === "underperforming" && cpa > 400) {
    return { message: "Reduce spend — high CPA with low conversion", severity: "warning", icon: "reduce" };
  }
  if (rank === "underperforming") {
    return { message: "Reduce spend — low conversion rate", severity: "warning", icon: "reduce" };
  }
  if (leadQualityPct < 55) {
    return { message: "Monitor performance — lead quality trending low", severity: "neutral", icon: "monitor" };
  }
  return { message: "Monitor performance — within expected range", severity: "neutral", icon: "monitor" };
}
